import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, FileText, Download, Eye, Pencil, Paperclip, Scale, Search, FilterX, ShieldCheck, Send, ScrollText } from "lucide-react";
import type { WorksPermitAnalysis, DecisionEvaluation } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { AttachmentField } from "@/components/AttachmentField";
import { TypedAttachmentField, type TypedAttachment } from "@/components/TypedAttachmentField";
import { AiAnalysisPanel } from "@/components/AiAnalysisPanel";
import { DecisionEvaluationPanel } from "@/components/DecisionEvaluationPanel";
import { RulePanel } from "@/components/RulePanel";
import { QueryError } from "@/components/QueryError";
import { RegulationConflictPanel, RegulationCheckPending } from "@/components/RegulationConflictPanel";
import { WorksPermitReviewModal, DecisionAuditSection, PERMIT_FOLDER, toRows, type WorksPermit } from "@/components/WorksPermitReviewModal";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type PermitForm = {
  workType: string;
  description: string;
  startDate: string;
  estimatedEndDate: string;
  applicantName: string;
  applicantPhone: string;
  applicantPhone2: string;
  applicantEmail: string;
  applicantEmail2: string;
  contractorName: string;
  contractorCompany: string;
  contractorPhone: string;
  contractorPhone2: string;
  contractorEmail: string;
  contractorEmail2: string;
  plansDelivered: boolean;
  rulesAccepted: boolean;
  rulesAcceptedName: string;
};

/** What the API needs from the form (empty strings become "not given"). */
const toPayload = (form: PermitForm, attachments: TypedAttachment[]) => ({
  ...form,
  applicantPhone: form.applicantPhone || null,
  applicantPhone2: form.applicantPhone2 || null,
  applicantEmail: form.applicantEmail || null,
  applicantEmail2: form.applicantEmail2 || null,
  contractorName: form.contractorName || null,
  contractorCompany: form.contractorCompany || null,
  contractorPhone: form.contractorPhone || null,
  contractorPhone2: form.contractorPhone2 || null,
  contractorEmail: form.contractorEmail || null,
  contractorEmail2: form.contractorEmail2 || null,
  rulesAcceptedName: form.rulesAcceptedName || form.applicantName || null,
  attachments: attachments.filter((a) => a.fileUrl && a.title.trim()),
});

const statusVariant: Record<string, "default" | "info" | "warning" | "success" | "danger"> = {
  SUBMITTED: "info",
  UNDER_REVIEW: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  COMPLETED: "default",
};

const isEditable = (s: WorksPermit["status"]) => s === "SUBMITTED" || s === "UNDER_REVIEW";

// Pending requests are the work at hand; decided ones (approved / denied) live on in their
// own tab, linked to their resolution, instead of mixing with the queue.
type View = "pending" | "decided" | "all";
const VIEW_STATUSES: Record<View, readonly WorksPermit["status"][]> = {
  pending: ["SUBMITTED", "UNDER_REVIEW"],
  decided: ["APPROVED", "REJECTED", "COMPLETED"],
  all: ["SUBMITTED", "UNDER_REVIEW", "APPROVED", "REJECTED", "COMPLETED"],
};
const VIEW_LABEL: Record<View, string> = { pending: "worksPermits.tabPending", decided: "worksPermits.tabDecided", all: "worksPermits.tabAll" };

export function WorksPermitsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reviewFor, setReviewFor] = useState<WorksPermit | null>(null);
  const [editFor, setEditFor] = useState<WorksPermit | null>(null);
  const [auditFor, setAuditFor] = useState<WorksPermit | null>(null);
  // Filters — all client-side over the loaded list, so the admin can slice the
  // permits any way she likes (text across every field, status, unit, type, dates).
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [view, setView] = useState<View | null>(null);

  const { data: permits = [], isLoading, isError } = useQuery({
    queryKey: ["works-permits"],
    queryFn: async () => (await api.get<WorksPermit[]>("/works-permits")).data,
  });

  const canReview = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER";
  // The administration and the Junta start on what is pending; an owner sees all of theirs,
  // so an approved request (and its signed resolution) is right there.
  const activeView: View = view ?? (canReview ? "pending" : "all");
  const viewCount = (v: View) => permits.filter((p) => VIEW_STATUSES[v].includes(p.status)).length;
  const switchView = (v: View) => {
    setView(v);
    setStatusFilter("");
  };

  // ?permit=<id> (e.g. from a resolution's "Ver solicitud completa"): bring that card
  // into view and mark it, with the filters cleared so it is not hidden.
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("permit");
  useEffect(() => {
    if (!focusId || permits.length === 0) return;
    clearFilters();
    setView("all"); // a decided request must not stay hidden behind the Pending tab
    requestAnimationFrame(() => document.getElementById(`permit-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, permits.length]);

  const sendMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/works-permits/${id}/send-to-resolutions`)).data,
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["resolutions"] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });
  const canOnBehalf =
    user?.role === "ADMIN" || (user?.permissions?.includes("onbehalf:works_permits") ?? false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["works-permits"] });

  // Admin-only hard delete (the API enforces the role); the audit log keeps the tally.
  const clearFlagMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) =>
      (await api.post(`/works-permits/${id}/clear-flag`, { note })).data,
    onSuccess: invalidate,
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/works-permits/${id}`)).data,
    onSuccess: invalidate,
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });

  const unitOptions = Array.from(new Set(permits.map((p) => p.unit.unitNumber))).sort();
  const typeOptions = Array.from(new Set(permits.map((p) => p.workType))).sort();
  const needle = search.trim().toLowerCase();
  const visible = permits.filter((p) => {
    if (!VIEW_STATUSES[activeView].includes(p.status)) return false;
    if (statusFilter && p.status !== statusFilter) return false;
    if (unitFilter && p.unit.unitNumber !== unitFilter) return false;
    if (typeFilter && p.workType !== typeFilter) return false;
    if (fromDate && p.startDate.slice(0, 10) < fromDate) return false;
    if (toDate && p.startDate.slice(0, 10) > toDate) return false;
    if (!needle) return true;
    const haystack = [
      p.unit.unitNumber,
      p.unit.ownerName,
      `${p.submittedBy.firstName} ${p.submittedBy.lastName}`,
      p.reviewedBy ? `${p.reviewedBy.firstName} ${p.reviewedBy.lastName}` : "",
      p.workType,
      p.description,
      p.contractorName ?? "",
      p.contractorPhone ?? "",
      p.reviewNotes ?? "",
      p.status,
      t(`worksPermits.${p.status.toLowerCase()}` as any),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
  const filtersActive = !!(needle || statusFilter || unitFilter || typeFilter || fromDate || toDate);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setUnitFilter("");
    setTypeFilter("");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={24} /> {t("worksPermits.title")}
        </h1>
        {(user?.unitId || canOnBehalf) && (
          <button onClick={() => setOpen(true)} className="btn-primary">
            <Plus size={16} /> {t("worksPermits.submit")}
          </button>
        )}
      </div>

      {permits.length > 0 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("worksPermits.title")}>
          {(["pending", "decided", "all"] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={activeView === v}
              className={`${activeView === v ? "btn-primary" : "btn-secondary"} text-sm`}
              onClick={() => switchView(v)}
            >
              {t(VIEW_LABEL[v])} ({viewCount(v)})
            </button>
          ))}
        </div>
      )}

      {permits.length > 0 && (
        <div className="card space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            <div className="md:col-span-2 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                className="input pl-8"
                placeholder={t("worksPermits.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label={t("worksPermits.filterStatus")}>
              <option value="">{t("worksPermits.allStatuses")}</option>
              {VIEW_STATUSES[activeView].map((s) => (
                <option key={s} value={s}>{t(`worksPermits.${s.toLowerCase()}` as any)}</option>
              ))}
            </select>
            <select className="input" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} aria-label={t("worksPermits.filterUnit")}>
              <option value="">{t("worksPermits.allUnits")}</option>
              {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <select className="input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label={t("worksPermits.filterType")}>
              <option value="">{t("worksPermits.allTypes")}</option>
              {typeOptions.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <div className="md:col-span-2 flex items-center gap-1">
              <input type="date" className="input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label={t("worksPermits.dateFrom")} title={t("worksPermits.dateFrom")} />
              <span className="text-slate-400 text-xs">–</span>
              <input type="date" className="input" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label={t("worksPermits.dateTo")} title={t("worksPermits.dateTo")} />
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{t("worksPermits.results", { shown: visible.length, total: viewCount(activeView) })}</span>
            {filtersActive && (
              <button type="button" className="inline-flex items-center gap-1 text-primary hover:underline" onClick={clearFilters}>
                <FilterX size={12} /> {t("worksPermits.clearFilters")}
              </button>
            )}
          </div>
        </div>
      )}

      {isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : permits.length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : visible.length === 0 ? (
        <div className="card text-slate-500">{t("worksPermits.noMatches")}</div>
      ) : (
        <div className="space-y-4">
          {visible.map((p) => {
            const canEditThis =
              isEditable(p.status) && (canReview || canOnBehalf || user?.unitId === p.unitId);
            const showAnalysis = p.aiAnalysis && (canReview || !!p.reviewedAt);
            const signed = p.resolution?.attachments?.[0];
            return (
              <div
                key={p.id}
                id={`permit-${p.id}`}
                className={`card space-y-3 scroll-mt-4 ${focusId === p.id ? "ring-2 ring-primary" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={statusVariant[p.status]}>{t(`worksPermits.${p.status.toLowerCase()}` as any)}</Badge>
                      <span className="text-xs text-slate-500">Unidad {p.unit.unitNumber} · {p.submittedBy.firstName} {p.submittedBy.lastName}</span>
                      {p.enteredOnBehalf && <Badge variant="default">{t("worksPermits.onBehalf")}</Badge>}
                      {p.resolution && canReview && (
                        <Link
                          to={`/resolutions#res-${p.resolution.id}`}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ScrollText size={11} /> {t("worksPermits.inResolutions")} →
                        </Link>
                      )}
                      {p.aiFlagged && (
                        <Badge variant="warning">
                          <AlertTriangle size={11} className="mr-1" /> {t("worksPermits.flagged")}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold">{p.workType}</h3>
                    <p className="text-sm text-slate-600 mt-1">{p.description}</p>
                    <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-3">
                      <span>{t("worksPermits.requestDate")}: {formatDate(p.submittedAt, i18n.language)}</span>
                      <span>{t("worksPermits.startDate")}: {formatDateOnly(p.startDate, i18n.language)}</span>
                      <span>{t("worksPermits.estimatedEndDate")}: {formatDateOnly(p.estimatedEndDate, i18n.language)}</span>
                      <span>{t("worksPermits.plansDeliveredShort")}: {p.plansDelivered ? t("worksPermits.yes") : t("worksPermits.no")}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                      {(p.applicantName || p.applicantPhone || p.applicantEmail) && (
                        <span>
                          {t("worksPermits.sectionApplicant")}: {[p.applicantName, p.applicantPhone, p.applicantEmail].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      {(p.contractorName || p.contractorCompany) && (
                        <span>
                          {t("worksPermits.contractorName")}: {[p.contractorName, p.contractorCompany, p.contractorPhone, p.contractorEmail].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                    {toRows(p).length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="font-medium text-slate-600">{t("worksPermits.supportingDocs")}:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {toRows(p).map((a, i) => (
                            <a key={i} href={a.fileUrl} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-primary hover:underline">
                              <Paperclip size={11} />
                              <span className="text-slate-500">{t(`worksPermits.docKinds.${a.kind}`)}:</span> {a.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-2 text-xs inline-flex items-center gap-1">
                      <ShieldCheck size={12} className={p.rulesAcceptedAt ? "text-emerald-600" : "text-slate-400"} />
                      {p.rulesAcceptedAt
                        ? t("worksPermits.certifiedBy", { name: p.rulesAcceptedName ?? p.applicantName ?? "", date: formatDate(p.rulesAcceptedAt, i18n.language) })
                        : t("worksPermits.notCertified")}
                    </div>
                    {p.reviewNotes && (
                      <div className="mt-2 p-2 bg-slate-50 rounded text-sm">
                        <strong>{t("worksPermits.reviewNotes")}:</strong> {p.reviewNotes}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {/* Once the signed resolution is uploaded it replaces the unsigned PDF. */}
                    {signed ? (
                      <a href={signed.fileUrl} target="_blank" rel="noreferrer" className="btn-secondary text-xs">
                        <Download size={12} /> {t("worksPermits.downloadSigned")}
                      </a>
                    ) : (
                      p.resolutionFileUrl && (
                        <a href={p.resolutionFileUrl} download={`resolucion-${p.id}.pdf`} className="btn-secondary text-xs">
                          <Download size={12} /> {t("worksPermits.downloadResolution")}
                        </a>
                      )
                    )}
                    {canEditThis && (
                      <button className="btn-secondary text-xs" onClick={() => setEditFor(p)}>
                        <Pencil size={12} /> {t("worksPermits.edit")}
                      </button>
                    )}
                    {canReview && isEditable(p.status) && !p.resolution && (
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => sendMutation.mutate(p.id)}
                        disabled={sendMutation.isPending}
                        title={t("worksPermits.sendToResolutionsHint")}
                      >
                        <Send size={12} /> {t("worksPermits.sendToResolutions")}
                      </button>
                    )}
                    {canReview && isEditable(p.status) && (
                      <button className="btn-primary text-xs" onClick={() => setReviewFor(p)}>
                        <Eye size={12} /> {t("worksPermits.review")}
                      </button>
                    )}
                    {canReview && (p.status === "APPROVED" || p.status === "REJECTED") && (
                      <button className="btn-secondary text-xs" onClick={() => setAuditFor(p)}>
                        <Scale size={12} /> {t("worksPermits.evaluateDecision")}
                      </button>
                    )}
                    <AdminRecordActions
                      entityLabel={t("worksPermits.deleteEntity", { type: p.workType, unit: p.unit.unitNumber })}
                      onDelete={() => deleteMutation.mutate(p.id)}
                      disabled={deleteMutation.isPending}
                    />
                  </div>
                </div>
                {/* The conflict notice goes to the owner as well as the board — unlike the
                    full analysis below, which carries a recommendation for the Junta and
                    stays hidden from the applicant until the Junta has decided. */}
                {p.aiFlagged && p.aiAnalysis && (
                  <div className="space-y-2">
                    <RegulationConflictPanel analysis={p.aiAnalysis as WorksPermitAnalysis} />
                    {canReview && (
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                        disabled={clearFlagMutation.isPending}
                        onClick={() => {
                          const note = window.prompt(t("worksPermits.clearFlagPrompt"));
                          if (note !== null) clearFlagMutation.mutate({ id: p.id, note });
                        }}
                      >
                        {clearFlagMutation.isPending ? t("worksPermits.clearingFlag") : t("worksPermits.clearFlag")}
                      </button>
                    )}
                  </div>
                )}
                {!p.aiFlagged && p.aiFlagClearedAt && (
                  <RegulationConflictPanel
                    analysis={p.aiAnalysis as WorksPermitAnalysis}
                    clearedNote={p.aiFlagClearedNote ?? ""}
                  />
                )}
                {!p.aiAnalyzedAt && p.aiCheckStartedAt && <RegulationCheckPending />}
                {showAnalysis && <AiAnalysisPanel analysis={p.aiAnalysis as WorksPermitAnalysis} defaultOpen={false} />}
                {canReview && p.aiDecisionEval && (
                  <DecisionEvaluationPanel evaluation={p.aiDecisionEval as DecisionEvaluation} defaultOpen={false} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <SubmitModal open={open} onClose={() => setOpen(false)} ownUnitId={user?.unitId ?? ""} canOnBehalf={canOnBehalf} onDone={invalidate} />
      {editFor && <EditModal permit={editFor} onClose={() => setEditFor(null)} onDone={invalidate} />}
      {auditFor && <DecisionAuditModal permit={auditFor} onClose={() => setAuditFor(null)} onDone={invalidate} />}
      <WorksPermitReviewModal permit={reviewFor} onClose={() => setReviewFor(null)} onDone={invalidate} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded border border-slate-200 p-3 space-y-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</legend>
      {children}
    </fieldset>
  );
}

/** The official "Solicitud de Permiso de Obras y/o Trabajos", section by section. */
function PermitFields({
  form,
  setForm,
  attachments,
  setAttachments,
  unitNumber,
}: {
  form: PermitForm;
  setForm: (f: PermitForm) => void;
  attachments: TypedAttachment[];
  setAttachments: (a: TypedAttachment[]) => void;
  unitNumber?: string;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<PermitForm>) => setForm({ ...form, ...patch });
  return (
    <>
      <Section title={t("worksPermits.sectionApplicant")}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_10rem] gap-3">
          <div>
            <label className="label">{t("worksPermits.applicantName")}</label>
            <input className="input" required value={form.applicantName} onChange={(e) => set({ applicantName: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.apartmentNumber")}</label>
            <input className="input bg-slate-50" value={unitNumber ?? ""} readOnly />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("worksPermits.phone1")}</label>
            <input className="input" value={form.applicantPhone} onChange={(e) => set({ applicantPhone: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.phone2")}</label>
            <input className="input" value={form.applicantPhone2} onChange={(e) => set({ applicantPhone2: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.email1")}</label>
            <input type="email" className="input" value={form.applicantEmail} onChange={(e) => set({ applicantEmail: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.email2")}</label>
            <input type="email" className="input" value={form.applicantEmail2} onChange={(e) => set({ applicantEmail2: e.target.value })} />
          </div>
        </div>
      </Section>

      <Section title={t("worksPermits.sectionContractor")}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("worksPermits.contractorName")}</label>
            <input className="input" value={form.contractorName} onChange={(e) => set({ contractorName: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.contractorCompany")}</label>
            <input className="input" value={form.contractorCompany} onChange={(e) => set({ contractorCompany: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.phone1")}</label>
            <input className="input" value={form.contractorPhone} onChange={(e) => set({ contractorPhone: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.phone2")}</label>
            <input className="input" value={form.contractorPhone2} onChange={(e) => set({ contractorPhone2: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.email1")}</label>
            <input type="email" className="input" value={form.contractorEmail} onChange={(e) => set({ contractorEmail: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.email2")}</label>
            <input type="email" className="input" value={form.contractorEmail2} onChange={(e) => set({ contractorEmail2: e.target.value })} />
          </div>
        </div>
      </Section>

      <Section title={t("worksPermits.sectionWorks")}>
        <div>
          <label className="label">{t("worksPermits.workType")}</label>
          <input className="input" required value={form.workType} onChange={(e) => set({ workType: e.target.value })} placeholder={t("worksPermits.workTypePlaceholder")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("worksPermits.constructionFrom")}</label>
            <input type="date" className="input" required value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("worksPermits.constructionTo")}</label>
            <input type="date" className="input" required value={form.estimatedEndDate} onChange={(e) => set({ estimatedEndDate: e.target.value })} />
          </div>
        </div>
        <div>
          <span className="label">{t("worksPermits.plansDelivered")}</span>
          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" name="plansDelivered" checked={form.plansDelivered} onChange={() => set({ plansDelivered: true })} /> {t("worksPermits.yes")}
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" name="plansDelivered" checked={!form.plansDelivered} onChange={() => set({ plansDelivered: false })} /> {t("worksPermits.no")}
            </label>
          </div>
        </div>
        <div>
          <label className="label">{t("worksPermits.descriptionLong")}</label>
          <textarea className="input min-h-[120px]" required value={form.description} onChange={(e) => set({ description: e.target.value })} />
          <p className="mt-1 text-xs text-slate-500">{t("worksPermits.descriptionHint")}</p>
        </div>
      </Section>

      <Section title={t("worksPermits.attachments")}>
        <TypedAttachmentField label={t("worksPermits.attachmentsLabel")} folder={PERMIT_FOLDER} value={attachments} onChange={setAttachments} />
      </Section>

      <Section title={t("worksPermits.sectionCertification")}>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" required checked={form.rulesAccepted} onChange={(e) => set({ rulesAccepted: e.target.checked })} />
          <span>{t("worksPermits.certification")}</span>
        </label>
        <div>
          <label className="label">{t("worksPermits.certifiedName")}</label>
          <input className="input" required value={form.rulesAcceptedName} onChange={(e) => set({ rulesAcceptedName: e.target.value })} placeholder={form.applicantName} />
        </div>
      </Section>
    </>
  );
}

const emptyForm: PermitForm = {
  workType: "",
  description: "",
  startDate: "",
  estimatedEndDate: "",
  applicantName: "",
  applicantPhone: "",
  applicantPhone2: "",
  applicantEmail: "",
  applicantEmail2: "",
  contractorName: "",
  contractorCompany: "",
  contractorPhone: "",
  contractorPhone2: "",
  contractorEmail: "",
  contractorEmail2: "",
  plansDelivered: false,
  rulesAccepted: false,
  rulesAcceptedName: "",
};

interface UnitOption {
  id: string;
  unitNumber: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  ownerPhone?: string | null;
}

function SubmitModal({ open, onClose, ownUnitId, canOnBehalf, onDone }: { open: boolean; onClose: () => void; ownUnitId: string; canOnBehalf: boolean; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<PermitForm>(emptyForm);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [attachments, setAttachments] = useState<TypedAttachment[]>([]);

  const { data: units = [] } = useQuery({
    queryKey: ["units-picker"],
    queryFn: async () => (await api.get<UnitOption[]>("/units")).data,
    enabled: open && canOnBehalf,
  });
  const effectiveUnitId = ownUnitId || selectedUnitId;
  // The "Titular" block defaults to the unit's registered owner (a resident may only read their own unit).
  const { data: unit } = useQuery({
    queryKey: ["unit", effectiveUnitId],
    queryFn: async () => (await api.get<UnitOption>(`/units/${effectiveUnitId}`)).data,
    enabled: open && !!effectiveUnitId,
  });
  useEffect(() => {
    if (!unit) return;
    setForm((f) => ({
      ...f,
      applicantName: f.applicantName || unit.ownerName || "",
      applicantEmail: f.applicantEmail || unit.ownerEmail || "",
      applicantPhone: f.applicantPhone || unit.ownerPhone || "",
      rulesAcceptedName: f.rulesAcceptedName || unit.ownerName || "",
    }));
  }, [unit]);

  const mutation = useMutation({
    mutationFn: async () => (await api.post("/works-permits", { ...toPayload(form, attachments), unitId: effectiveUnitId })).data,
    onSuccess: () => {
      onDone();
      onClose();
      setForm(emptyForm);
      setSelectedUnitId("");
      setAttachments([]);
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={t("worksPermits.formTitle")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <RulePanel tags={["works-permit", "architectural"]} />
        {!ownUnitId && canOnBehalf && (
          <div>
            <label className="label">{t("worksPermits.unit")}</label>
            <select className="input" required value={selectedUnitId} onChange={(e) => { setSelectedUnitId(e.target.value); setForm((f) => ({ ...f, applicantName: "", applicantEmail: "", applicantPhone: "", rulesAcceptedName: "" })); }}>
              <option value="">—</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.unitNumber}{u.ownerName ? ` · ${u.ownerName}` : ""}</option>
              ))}
            </select>
          </div>
        )}
        <PermitFields form={form} setForm={setForm} attachments={attachments} setAttachments={setAttachments} unitNumber={unit?.unitNumber} />
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending || !effectiveUnitId}>{t("worksPermits.submit")}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditModal({ permit, onClose, onDone }: { permit: WorksPermit; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<PermitForm>({
    workType: permit.workType,
    description: permit.description,
    startDate: permit.startDate.slice(0, 10),
    estimatedEndDate: permit.estimatedEndDate.slice(0, 10),
    applicantName: permit.applicantName ?? permit.unit.ownerName ?? "",
    applicantPhone: permit.applicantPhone ?? "",
    applicantPhone2: permit.applicantPhone2 ?? "",
    applicantEmail: permit.applicantEmail ?? "",
    applicantEmail2: permit.applicantEmail2 ?? "",
    contractorName: permit.contractorName ?? "",
    contractorCompany: permit.contractorCompany ?? "",
    contractorPhone: permit.contractorPhone ?? "",
    contractorPhone2: permit.contractorPhone2 ?? "",
    contractorEmail: permit.contractorEmail ?? "",
    contractorEmail2: permit.contractorEmail2 ?? "",
    plansDelivered: !!permit.plansDelivered,
    rulesAccepted: !!permit.rulesAcceptedAt,
    rulesAcceptedName: permit.rulesAcceptedName ?? permit.applicantName ?? "",
  });
  const [attachments, setAttachments] = useState<TypedAttachment[]>(toRows(permit));

  const mutation = useMutation({
    mutationFn: async () => (await api.put(`/works-permits/${permit.id}`, toPayload(form, attachments))).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title={t("worksPermits.editTitle")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <RulePanel tags={["works-permit", "architectural"]} />
        <PermitFields form={form} setForm={setForm} attachments={attachments} setAttachments={setAttachments} unitNumber={permit.unit.unitNumber} />
        <p className="text-xs text-slate-500">{t("worksPermits.editNote")}</p>
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function DecisionAuditModal({ permit, onClose, onDone }: { permit: WorksPermit; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal open onClose={onClose} title={`${t("worksPermits.evaluateDecision")} — ${permit.workType}`} size="lg">
      <div className="space-y-3 text-sm">
        <div className="bg-slate-50 p-3 rounded">
          <p><strong>Unidad:</strong> {permit.unit.unitNumber} · {permit.unit.ownerName}</p>
          <p className="mt-1"><Badge variant={statusVariant[permit.status]}>{t(`worksPermits.${permit.status.toLowerCase()}` as any)}</Badge></p>
          <p className="mt-2 whitespace-pre-wrap text-slate-700">{permit.description}</p>
          {permit.reviewNotes && (
            <div className="mt-2">
              <strong>{t("worksPermits.reviewNotes")}:</strong>
              <p className="whitespace-pre-wrap text-slate-700">{permit.reviewNotes}</p>
            </div>
          )}
        </div>
        <RulePanel tags={["works-permit", "architectural"]} />
        <DecisionAuditSection permit={permit} onDone={onDone} />
      </div>
    </Modal>
  );
}
